import type { IDateValue } from "../../../utils";
import {
    AttachmentPreview,
    AttachmentPreviewRef,
    BodyContentType,
    LinkedEventDetails,
    LinkObject,
    MailAIDetails,
    MailAttachment,
    MailComponentSize,
    MailScore,
    MailSender,
    MailViewContent,
    SmartLabel,
} from "./mail";

/**
 * Contenu « widgets » (attachments/links/events) d'un lot de mails (batch — POST /mails/content).
 * Alimente le cache par-mail du SDK. Données brutes par mail (dédup/filtrage faits au read côté SDK).
 * Chaque entrée porte son internetMessageId (clé de regroupement / clé du Record).
 */
export type GetMailsContentRequestBody = {
    internetMessageIds: string[];
};

type MailViewContentEntry = MailViewContent & {
    internetMessageId: string;
};

export type GetMailsContentResponseBody = MailViewContentEntry[];

/**
 * Récupération à la demande des previews de pièces jointes (batch — POST /mails/attachments/previews).
 * Envoie les références (identité mail stable + id PJ), reçoit les previews résolues.
 */
export type GetAttachmentsPreviewsRequestBody = {
    attachments: AttachmentPreviewRef[];
};

export type GetAttachmentsPreviewsResponseBody = AttachmentPreview[];

export type GetMailsFromInternetMessageIdsResponseBody = {
    baseMails: BaseMail[];
    qualifiedMails: QualifiedMail[];
    /** Mails absents, sur réponse EXPLICITE du provider. */
    notFoundMailsInternetMessageIds: string[];
    /**
     * Mails dont le sort n'a PAS pu être tranché : le provider n'a pas répondu pour eux. Ni trouvés,
     * ni absents.
     */
    unresolvedMailsInternetMessageIds?: string[];
};

/**
 * `mails` = toutes les occurrences trouvées, sans canonicalisation — à ne jamais traiter comme
 * les `baseMails` de la route inbox, qui partent en analyse IA + DB.
 * `deletedInternetMessageIds` = absences CONFIRMÉES ; absent des trois listes = statu quo.
 */
export type GetMailsInAnyFolderResponseBody = {
    qualifiedMails: QualifiedMail[];
    mails: BaseMail[];
    deletedInternetMessageIds: string[];
};

/**
 * (AB#1645) Réponse de la vérif d'existence LÉGÈRE (verdicts-only). `internetMessageIdsToRemove` =
 * mails confirmés absents ∪ inbox-canonique-disparue → à retirer du store SmartLabel (compteurs).
 */
export type VerifyMailsExistenceResponseBody = {
    internetMessageIdsToRemove: string[];
};

export type BaseMail = {
    /** Identifiant unique interne */
    id: string;
    /** Identifiant du message Internet unique (format RFC 5322) */
    internetMessageId: string;
    /** Identifiant de conversation */
    conversationId: string;
    /** Dossier contenant l'email */
    parentFolderId: string;
    /** Sujet de l'email */
    subject: string;
    /** Contenu complet du message */
    message: string;
    /** Contenu du message sans les réponses précédentes*/
    uniqueBody: string;
    /** Aperçu court du message */
    messagePreview: string;
    /** Expéditeur de l'email */
    from: MailSender;
    /** Liste des destinataires */
    to: MailSender[];
    /** Liste des destinataires copie */
    cc: MailSender[];
    /** Liste des destinataires copie */
    bcc: MailSender[];
    /** Date et heure d'envoi */
    sentDateTime: IDateValue;
    /** Liste des pièces jointes */
    attachments: MailAttachment[];
    /** Indique si l'email a des pièces jointes */
    hasAttachment: boolean;
    /** Indique si l'email a été lu */
    isRead: boolean;
    /** Indique si l'email est marqué comme favori */
    isStarred: boolean;
    /** Indique si l'email est marqué comme important */
    isImportant: boolean;
    /** Liste des identifiants de labels associés */
    labelIds: string[];
    /** Détails d'un événement lié (si l'email est une invitation) */
    linkedEventDetails?: LinkedEventDetails;
    /** Indique si l'email référence une réunion */
    hasLinkedMeeting: boolean;
    /** Links found in mail unique body */
    links: LinkObject[];
    /** Taille du composant mail dans l'affichage Mosaique */
    componentSize: MailComponentSize;
    /** Type de contenu du body principal (text ou html) */
    bodyContentType: BodyContentType;
    /** Type de contenu du uniqueBody — body nettoyé sans les réponses précédentes (text ou html) */
    uniqueBodyContentType: BodyContentType;
};

export type EnrichedMail = BaseMail & {
    aiDetails: MailAIDetails;
};

type RankedMail = EnrichedMail & {
    /** Score du mail après analyse */
    score: number;
    /** Score de classement calculé pour l'email (optionnel) */
    _ranking: MailScore;
    /** Hash du context utilisé pour le calcul du ranking (optionnel) */
    _rankingContextHash: string;
};

export type QualifiedMail = RankedMail & {
    /** Indique si le mail est focus */
    isFocus: boolean;
    /** SmartLabel si il en possède un */
    smartLabel: SmartLabel;
    /** Indique si un mail a été manipulé par le user */
    isUserEdited: boolean;
    /** Indique si l'utilisateur a traité cet email */
    isUserProcessed: boolean;
    /** Date ISO copiée de sentDateTime si l'expéditeur est Mailvista, sinon null. Clé tri Focus groupe 1. */
    sentDateTimeMailvista: string | null;
};

// Type guard
export function isQualifiedMail(
    mail: BaseMail | QualifiedMail,
): mail is QualifiedMail {
    return "isFocus" in mail;
}

// ── PATCH /v1/mails/smart-label ──────────────────────────────────────────────
// WS6c / déc. #8 : `uniqueMailvistaId` dans le BODY (route statique ; bug encodage IIS sur le path).

/** Corps de la requête — déplacement manuel vers un autre SmartLabel */
export type SetMailSmartLabelRequestBody = {
    uniqueMailvistaId: string;
    smartLabel: SmartLabel;
    isUserEdited: true; // toujours true pour une action utilisateur
};

/**
 * Champs retournés par le backend après application des règles métier.
 * Plusieurs champs peuvent avoir été modifiés par rapport à la valeur demandée
 * (ex. isUserProcessed forcé à false si le mail passe hors Traités, isFocus recalculé, etc.)
 */
export type SetMailSmartLabelResponseDTO = {
    internetMessageId: string;
    smartLabel: SmartLabel;
    isUserEdited: boolean;
    isUserProcessed: boolean;
    isFocus: boolean;
};

// ── PATCH /v1/mails/focus ─────────────────────────────────────────────────────
// WS6c / déc. #8 : `uniqueMailvistaId` dans le BODY (route statique ; bug encodage IIS sur le path).

export type SetMailFocusRequestBody = {
    uniqueMailvistaId: string;
    isFocus: boolean;
};

export type SetMailFocusResponseDTO = {
    internetMessageId: string;
    smartLabel: SmartLabel;
    isUserEdited: boolean;
    isUserProcessed: boolean;
    isFocus: boolean;
    manualFocusDate?: string | null;
    categories?: string[];
};

// ── PATCH /v1/mails/processed ─────────────────────────────────────────────────
// WS6c / déc. #8 : `uniqueMailvistaId` dans le BODY (route statique ; bug encodage IIS sur le path).

export type SetMailProcessedRequestBody = {
    uniqueMailvistaId: string;
    isUserProcessed: boolean;
};

export type SetMailProcessedResponseDTO = {
    internetMessageId: string;
    smartLabel: SmartLabel;
    isUserEdited: boolean;
    isUserProcessed: boolean;
    isFocus: boolean;
};

export type ForwardMailRequestBody = {
    forwardId: string;
    /** Format `internetMessageId::parentFolderId`. */
    uniqueMailvistaId: string;
    toRecipients: string[];
    ccRecipients: string[];
    bccRecipients: string[];
    subject?: string;
    note: string;
    quotedBody?: string;
    removedAttachments: { name: string; contentType: string }[];
    newAttachments: {
        name: string;
        contentType: string;
        contentBytes: string;
    }[];
};

export type ForwardMailResponseBody = {
    internetMessageId: string;
};
